import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Order, getErrorMessage } from '../types';
import api from '../api';
import { useTranslation } from '../i18n';
import { formatDate } from '../formatDate';

const STATUS_LABELS: Record<number, string> = {
  0: 'PendingApproval',
  1: 'Confirmed',
  2: 'Expired',
  3: 'Returned',
  4: 'Cancelled',
};

function statusLabel(status: number): string {
  return STATUS_LABELS[status] ?? 'PendingApproval';
}

export const Orders: React.FC = () => {
  const { t, lang } = useTranslation();
  const [orders, setOrders] = useState<Order[]>([]);
  const [error, setError] = useState('');

  const fetchOrders = useCallback(async () => {
    try {
      const res = await api.get<Order[]>(`/orders`);
      setOrders(res.data);
    } catch (err) { setError(getErrorMessage(err, t('orders.load_error'))); }
  }, [t]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  return (
    <div>
      <h2 className="section-title">{t('orders.title')}</h2>
      {error && (
        <div className="alert alert-error">
          {error}
          <button className="dismiss" onClick={() => setError('')}>&times;</button>
        </div>
      )}
      {orders.length === 0 && !error && <p className="text-muted">{t('orders.none')}</p>}
      {orders.map(o => (
        <Link key={o.id} to={`/orders/${o.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
          <div className="card order-card" style={{ cursor: 'pointer' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <div>
                <h3>{t('orders.order_id', { id: o.id })}</h3>
                <p className="order-meta">
                  {t('orders.placed')} {formatDate(o.orderDate, lang)}
                  &nbsp;&middot;&nbsp;
                  {t('orders.rental')} {formatDate(o.rentalStartDate, lang)} &ndash; {formatDate(o.rentalEndDate, lang)}
                </p>
                <p className="order-meta">
                  <span className={`order-status order-status-${o.status}`}>
                    {t(`orders.status.${statusLabel(o.status)}`)}
                  </span>
                  {o.role && (
                    <>
                      &nbsp;&nbsp;
                      <span style={{
                        display: 'inline-block',
                        padding: '0.15rem 0.55rem',
                        borderRadius: '4px',
                        fontSize: '0.78rem',
                        fontWeight: 600,
                        background: o.role === 'lender' ? '#e3f2fd' : '#f3e5f5',
                        color: o.role === 'lender' ? '#1565c0' : '#7b1fa2',
                      }}>
                        {t(`dashboard.role.${o.role}`)}
                      </span>
                    </>
                  )}
                </p>
                <ul style={{ margin: '0.5rem 0', paddingLeft: '1.2rem' }}>
                  {o.items.map(i => (
                    <li key={i.id} style={{ fontSize: '0.9rem' }}>{i.product?.name} &times; {i.quantity}</li>
                  ))}
                </ul>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="order-total">{t('orders.total', { amount: o.totalAmount })}</div>
              </div>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
};
